import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SharedHeader from './SharedHeader';
import Footer from './Footer';
import { Calendar, Clock, ArrowLeft, Share2 } from 'lucide-react';
import { blogArticles } from '../data/blogArticles';
import { useSEO } from '../hooks/useSEO';

const formatText = (text: string) => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      return <strong key={index} className="font-bold text-white">{part}</strong>;
    }
    return part;
  });
};

const BlogPost = () => {
  const { postId } = useParams<{ postId: string }>();
  const { language } = useLanguage();

  const article = blogArticles[language]?.[postId || ''];
  
  useSEO({
    title: article ? `${article.title} - QRCodeForge` : (language === 'zh' ? '文章未找到 - QRCodeForge' : 'Article Not Found - QRCodeForge'),
    description: article ? article.excerpt : '',
    path: article ? `/blog/${postId}` : undefined
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <SharedHeader />
        <div className="relative pt-32 pb-16 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">
            {language === 'zh' ? '文章未找到' : 'Article Not Found'}
          </h1>
          <Link
            to={`/${language}/blog`}
            className="text-blue-300 hover:text-blue-200 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'zh' ? '返回博客' : 'Back to Blog'}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SharedHeader />

      {/* 动态背景元素 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      <div className="relative">
        {/* Back Button */}
        <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
          <Link
            to={`/${language}/blog`}
            className="text-blue-300 hover:text-blue-200 inline-flex items-center gap-2 mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === 'zh' ? '返回博客' : 'Back to Blog'}
          </Link>
        </div>

        {/* Article Header */}
        <header className="max-w-4xl mx-auto px-4 pb-8">
          <div className="mb-4">
            <span className="text-sm font-semibold text-blue-300 uppercase tracking-wide">
              {article.category}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {article.title}
          </h1>

          <div className="flex items-center gap-6 text-sm text-gray-300 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{article.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{article.readTime}</span>
            </div>
          </div>

          <p className="text-xl text-gray-300 leading-relaxed">
            {article.excerpt}
          </p>
        </header>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto px-4 pb-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 md:p-12">
            <div className="prose prose-invert prose-lg max-w-none">
              {article.content.map((section, index) => (
                <div key={index} className="mb-8">
                  {section.type === 'heading' && (
                    <h2 className="text-2xl font-bold text-white mb-4">
                      {section.text}
                    </h2>
                  )}
                  {section.type === 'paragraph' && (
                    <p className="text-gray-300 leading-relaxed mb-4">
                      {formatText(section.text || '')}
                    </p>
                  )}
                  {section.type === 'list' && (
                    <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                      {section.items?.map((item, i) => (
                        <li key={i} className="leading-relaxed">{formatText(item || '')}</li>
                      ))}
                    </ul>
                  )}
                  {section.type === 'subheading' && (
                    <h3 className="text-xl font-semibold text-white mb-3 mt-6">
                      {section.text}
                    </h3>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
            <h3 className="text-lg font-semibold text-white mb-2">
              {language === 'zh' ? '觉得这篇文章有用吗？' : 'Found this article helpful?'}
            </h3>
            <p className="text-gray-300 mb-4">
              {language === 'zh' ? '分享给您的朋友和同事' : 'Share it with your friends and colleagues'}
            </p>
            <button 
              onClick={() => {
                const url = window.location.href;
                const title = article.title;
                
                // 尝试使用Web Share API（移动设备）
                if (navigator.share) {
                  navigator.share({
                    title: title,
                    text: article.excerpt,
                    url: url
                  }).catch(() => {
                    // 如果用户取消分享，不做任何事
                  });
                } else {
                  // 桌面端：复制链接到剪贴板
                  navigator.clipboard.writeText(url).then(() => {
                    alert(language === 'zh' ? '链接已复制到剪贴板！' : 'Link copied to clipboard!');
                  }).catch(() => {
                    // 降级方案：显示链接
                    prompt(
                      language === 'zh' ? '复制此链接分享：' : 'Copy this link to share:',
                      url
                    );
                  });
                }
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              <Share2 className="w-4 h-4" />
              {language === 'zh' ? '分享文章' : 'Share Article'}
            </button>
          </div>

          {/* Related Articles */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-white mb-6">
              {language === 'zh' ? '相关文章' : 'Related Articles'}
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {article.relatedArticles?.map((relatedId, index) => {
                const related = blogArticles[language]?.[relatedId];
                if (!related) return null;
                return (
                  <Link
                    key={index}
                    to={`/${language}/blog/${relatedId}`}
                    className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/15 transition-all"
                  >
                    <h4 className="text-lg font-semibold text-white mb-2 hover:text-blue-300">
                      {related.title}
                    </h4>
                    <p className="text-sm text-gray-300 line-clamp-2">
                      {related.excerpt}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </div>
  );
};

export default BlogPost;
